import { Control, useForm, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { Modal } from '../../pure/modal/modal.component';
import { DialogActions, DialogContent, Grid, TextField } from '@mui/material';
import { Alert, AlertTitle } from '@mui/material';
import { parseErrorMessage } from '../../../../core/api/api.context';
import { TextButton } from '../../pure/text-button/text-button.component';
import { Dropdown } from '../../pure/dropdown/dropdown.component';
import { notePrivacyOptions } from './add-note-action.constants';
import {
  AddNoteActionHandlers,
  AddNoteActionProps,
  AddNoteActionState,
  KnownNoteEntityTypes,
  useAddNoteAction
} from './add-note.hook';
import { FormControl } from '../../pure/form-control/form-control.component';

export function AddNoteActionModal({ id, entityType, projectID, ...rest }: AddNoteActionProps) {
  const { state, handlers, hasCreatePrivateNotePermissions, isLoading } = useAddNoteAction({
    id,
    entityType,
    projectID,
    ...rest
  });

  // If the modal is loading we want to just show a basic empty modal until we have the data we need.
  if (isLoading) {
    return <Modal open={true} loading={true} title={'Add note'} maxWidth="sm" onClose={handlers.onCancel} />;
  }

  return (
    <AddNoteModalComponent
      entityType={entityType}
      state={state}
      handlers={handlers}
      loading={isLoading}
      hasCreatePrivateNotePermissions={hasCreatePrivateNotePermissions}
    />
  );
}

interface FormData {
  content: string;
  visibility: string; // public and private
}

interface DisplayNoteFieldsProps {
  register: UseFormRegister<FormData>;
  setValue: UseFormSetValue<FormData>;
  control: Control<FormData>;
  hasCreatePrivateNotePermissions: boolean;
}

export function AddNoteModalComponent({
  entityType,
  hasCreatePrivateNotePermissions,
  state,
  handlers,
  loading
}: {
  entityType: KnownNoteEntityTypes;
  hasCreatePrivateNotePermissions: boolean;
  state: AddNoteActionState;
  handlers: AddNoteActionHandlers;
  loading: boolean;
}) {
  const initialVisibility = hasCreatePrivateNotePermissions ? 'private' : 'public';

  const { register, handleSubmit, setValue, watch, control } = useForm<FormData>({
    //Defaults to private if they are a non-internal user and public otherwise.
    defaultValues: {
      visibility: initialVisibility,
      content: ''
    }
  });

  const onSubmit = (data: FormData) => {
    const { content, visibility } = data;
    handlers.onAddNote(content, visibility === 'private');
  };

  const visibility = watch('visibility');
  // The button colour should be primary if they don't have permissions
  // to create private notes or if they are creating a private note.
  const actionButtonColor = !hasCreatePrivateNotePermissions || visibility === 'private' ? 'primary' : 'danger';

  const actionButtonText = !hasCreatePrivateNotePermissions
    ? 'Add note'
    : visibility === 'private'
    ? 'Add private note'
    : 'Add public note';

  return (
    <Modal open={true} loading={loading} title={'Add note'} maxWidth="sm" onClose={handlers.onCancel}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent style={{ padding: '16px 24px', fontSize: '14px' }}>
          {state.error ? (
            <p>
              <Alert variant="outlined" severity="error">
                <AlertTitle>Something went wrong</AlertTitle>
                {parseErrorMessage(state.error.message)}
              </Alert>
              <p>{`There was an error with creating a note on this ${entityType}`}</p>
            </p>
          ) : null}
          {NoteFields({ register, setValue, control, hasCreatePrivateNotePermissions })}
        </DialogContent>
        <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
          <TextButton color="secondary" variant="outlined" onClick={handlers.onCancel}>
            Cancel
          </TextButton>
          <TextButton color={actionButtonColor} variant="outlined" type="submit">
            {actionButtonText}
          </TextButton>
        </DialogActions>
      </form>
    </Modal>
  );
}

function NoteFields({ control, hasCreatePrivateNotePermissions }: DisplayNoteFieldsProps) {
  return (
    <>
      {hasCreatePrivateNotePermissions ? (
        <>
          <Grid style={{ paddingBottom: '1rem' }}>
            <FormControl
              control={control}
              name="visibility"
              ComponentProps={{
                fullWidth: true,
                required: true,
                textFieldDesktopWidth: '100%',
                dropdownVariant: 'primarySolid' as const,
                placeholder: 'Select privacy',
                menuItems: notePrivacyOptions
              }}
              Component={Dropdown}
            />
          </Grid>
        </>
      ) : null}
      <Grid>
        <FormControl
          control={control}
          name="content"
          ComponentProps={{
            autoFocus: true,
            fullWidth: true,
            multiline: true,
            required: true,
            minRows: 5,
            type: 'text',
            variant: 'outlined' as const,
            placeholder: 'Note',
            style: { width: '100%' }
          }}
          Component={TextField}
        />
      </Grid>
    </>
  );
}
